/**
 * FMV lookup - turn a card or sealed-product row into a list of priced candidates.
 *
 * Cards:
 *   - Pokemon  -> api.pokemontcg.io (tcgplayer.prices.{variant}.market)
 *   - MTG      -> api.scryfall.com (prices.usd / prices.usd_foil)
 *   - graded slabs -> skip API; route user to eBay sold listings
 *
 * Sealed:
 *   - Linked productId -> sealed_current_prices (TCGCSV synced daily)
 *   - Otherwise -> sealed_products name search (offers to link on pick)
 *   - Fallback  -> TCGplayer external search link
 */
import { Settings } from "./prefs";
import { getCurrentSealedPrice, searchSealedProducts } from "./sealedTcgcsv";

export interface FmvCandidate {
  image?: string;
  name: string;
  set?: string;
  number?: string;
  variant?: string;
  unit: number;     // per-unit market price
  source: string;
}

export interface FmvResult {
  candidates?: FmvCandidate[];
  external?: string;
  externalLabel?: string;
  hint?: string;
  error?: string;
}

export function tcgPlayerSearchUrl(q: string): string {
  return "https://www.tcgplayer.com/search/all/product?q=" + encodeURIComponent(q);
}
export function ebaySoldSearchUrl(q: string): string {
  return "https://www.ebay.com/sch/i.html?_nkw=" + encodeURIComponent(q) + "&LH_Sold=1&LH_Complete=1";
}

export function isGradedCondition(c: string | undefined | null): boolean {
  if (!c) return false;
  const u = String(c).toUpperCase();
  return u.includes("PSA") || u.includes("BGS") || u.includes("CGC") || u.includes("SGC");
}

/**
 * A card LOT is "graded" when it has been slabbed (Inventory Class = "Slab") or
 * carries a recognizable grading-company grade in Condition/Grade. Graded cards
 * must be valued MANUALLY: raw-card price APIs and image matching return the
 * ungraded price, which materially under-values a slab (e.g. a PSA 10 is worth
 * far more than the raw card). Callers use this to skip auto-pricing.
 */
export function isGradedCard(row: Record<string, any> | undefined | null): boolean {
  if (!row) return false;
  if (String(row["Inventory Class"] || "").trim().toLowerCase() === "slab") return true;
  return isGradedCondition(row["Condition/Grade"]);
}

/**
 * Manual-FMV lock. When a lot is locked, the user has priced it on purpose
 * (manual entry) and NO automated pricing path may overwrite Current FMV:
 * not the daily auto-sweep, not "Refresh all FMVs", not future server-side
 * revaluation jobs. The per-row refresh button warns instead of silently applying.
 */
export function isFmvLocked(row: Record<string, any> | undefined | null): boolean {
  if (!row) return false;
  const v = row["FMV Locked"];
  return v === true || v === "true" || v === 1;
}

/** Strip characters the pokemontcg.io query analyzer can't match (δ, ★, ♀ …)
 *  while KEEPING accents/apostrophes it requires exactly (Flabébé, Cynthia's).
 *  "Typhlosion δ" → name:"Typhlosion" still finds the δ card. */
export function searchableCardName(name: string): string {
  return String(name || "")
    .replace(/\u2019/g, "'")
    .replace(/[^\p{Script=Latin}0-9'&.:!\- ]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

async function fetchPokemonCandidates(name: string, setName: string): Promise<FmvCandidate[]> {
  let q = `name:"${searchableCardName(name).replace(/"/g, "")}"`;
  if (setName) q += ` set.name:"${setName.replace(/"/g, "")}"`;
  const url = `https://api.pokemontcg.io/v2/cards?q=${encodeURIComponent(q)}&pageSize=20&orderBy=-set.releaseDate`;
  const headers: Record<string, string> = {};
  const key = Settings.get().pokemonTcgApiKey;
  if (key) headers["X-Api-Key"] = key;
  const r = await fetch(url, { headers });
  if (!r.ok) throw new Error("HTTP " + r.status);
  const j = await r.json();
  const cards: any[] = j.data || [];
  const out: FmvCandidate[] = [];
  const priorityOrder = [
    "holofoil", "reverseHolofoil", "normal",
    "1stEditionHolofoil", "unlimitedHolofoil", "1stEdition", "1stEditionNormal",
  ];
  for (const c of cards) {
    const prices = (c.tcgplayer && c.tcgplayer.prices) || {};
    const variants = Object.keys(prices);
    if (!variants.length) continue;
    variants.sort((a, b) => {
      const pa = priorityOrder.indexOf(a); const pb = priorityOrder.indexOf(b);
      return (pa < 0 ? 99 : pa) - (pb < 0 ? 99 : pb);
    });
    for (const v of variants) {
      const p = prices[v];
      if (p && typeof p.market === "number") {
        out.push({
          image: c.images && (c.images.small || c.images.large),
          name: c.name,
          set: c.set && c.set.name,
          number: c.number,
          variant: v,
          unit: p.market,
          source: `TCGplayer Market (${v})`,
        });
      }
    }
  }
  return out.slice(0, 30);
}

async function fetchMtgCandidates(name: string, setName: string): Promise<FmvCandidate[]> {
  let url: string;
  if (setName) {
    const q = `!"${name.replace(/"/g, "")}" set:"${setName.replace(/"/g, "")}"`;
    url = `https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}&unique=prints&order=released&dir=desc`;
  } else {
    url = `https://api.scryfall.com/cards/search?q=${encodeURIComponent('!"' + name.replace(/"/g, "") + '"')}&unique=prints&order=released&dir=desc`;
  }
  const r = await fetch(url);
  if (r.status === 404) return [];
  if (!r.ok) throw new Error("HTTP " + r.status);
  const j = await r.json();
  const cards: any[] = j.data || [];
  const out: FmvCandidate[] = [];
  for (const c of cards.slice(0, 16)) {
    const img =
      (c.image_uris && (c.image_uris.normal || c.image_uris.small)) ||
      (c.card_faces && c.card_faces[0] && c.card_faces[0].image_uris && c.card_faces[0].image_uris.normal);
    if (c.prices?.usd) {
      out.push({
        image: img, name: c.name, set: c.set_name, number: c.collector_number,
        variant: "USD (non-foil)", unit: Number(c.prices.usd), source: "Scryfall USD",
      });
    }
    if (c.prices?.usd_foil) {
      out.push({
        image: img, name: c.name, set: c.set_name, number: c.collector_number,
        variant: "USD foil", unit: Number(c.prices.usd_foil), source: "Scryfall USD foil",
      });
    }
  }
  return out;
}

export async function fmvLookupForCard(card: Record<string, any>): Promise<FmvResult> {
  const game = card["Game"] || "Pokemon";
  const name = (card["Card Name"] || "").trim();
  const setName = (card["Set"] || "").trim();
  const grade = (card["Condition/Grade"] || "").trim();
  if (!name) return { error: "Add a Card Name first." };
  if (isGradedCondition(grade)) {
    return {
      candidates: [],
      external: ebaySoldSearchUrl([name, setName, grade].filter(Boolean).join(" ")),
      externalLabel: "eBay sold listings",
      hint: "Graded slabs aren't priced by raw-card APIs. Use eBay sold listings.",
    };
  }
  let candidates: FmvCandidate[] = [];
  try {
    if (game === "Pokemon")                    candidates = await fetchPokemonCandidates(name, setName);
    else if (game === "Magic: The Gathering")  candidates = await fetchMtgCandidates(name, setName);
  } catch { /* fall through to external */ }
  return {
    candidates,
    external: tcgPlayerSearchUrl([name, setName].filter(Boolean).join(" ")),
    externalLabel: "TCGplayer search",
  };
}

export async function fmvLookupForSealed(s: Record<string, any>): Promise<FmvResult> {
  const product = (s["Product"] || "").trim();
  const setName = (s["Set"] || "").trim();
  const linkedPid = Number(s["TCG Product ID"]) || 0;
  if (!product && !linkedPid) return { error: "Add a Product name first." };

  const candidates: FmvCandidate[] = [];

  // Fast path: lot is already linked to a specific TCGCSV product -> fetch its
  // current price directly. Single, definitive candidate.
  if (linkedPid) {
    try {
      const p = await getCurrentSealedPrice(linkedPid);
      const price = p.market ?? p.mid ?? p.low ?? null;
      if (price != null) {
        candidates.push({
          name: product || `Product #${linkedPid}`,
          set: setName,
          variant: "Sealed (TCGplayer Market)",
          unit: price,
          source: `TCGplayer Market${p.date ? ` (${p.date})` : ""}`,
        });
      }
    } catch (e) {
      console.warn("[fmvLookupForSealed] linked productId lookup failed:", e);
    }
  }

  // Fuzzy path: no linked productId -> search catalog by name, return top
  // matches. User picks one; the picker also links the lot.
  if (!candidates.length && product) {
    try {
      const rows = await searchSealedProducts({ query: product, limit: 12 });
      for (const r of rows) {
        const price = r.market_price ?? r.mid_price ?? r.low_price ?? null;
        if (price == null) continue;
        candidates.push({
          image: r.image_url || undefined,
          name: r.name,
          set: r.group_name || setName,
          variant: r.product_kind || "Sealed",
          unit: price,
          source: "TCGplayer Market (via TCGCSV)",
        });
      }
    } catch (e) {
      console.warn("[fmvLookupForSealed] catalog search failed:", e);
    }
  }

  return {
    candidates,
    external: tcgPlayerSearchUrl([product, setName].filter(Boolean).join(" ")),
    externalLabel: "TCGplayer search",
    hint: candidates.length
      ? "Prices from the daily TCGCSV catalog sync. Pick one to set this lot's FMV."
      : linkedPid
        ? "No current price for the linked product. Refresh tomorrow or pick another product."
        : "No catalog match for that product name. Use the Browse catalog button to search, or enter the unit price manually.",
  };
}
